import { getFeatureWithFallback } from '../../utils/onthehouseParser'

// CRA uses REACT_APP_* env vars. Fallback keeps previous default.
export const API_BASE = import.meta.env.REACT_APP_API_URL || window.location.origin

export const extractValue = (data, prefix) => {
  console.log(`[extractValue] Looking for prefix "${prefix}" in data:`, data)
  if (!data) {
    console.log(`[extractValue] No data, returning N/A`)
    return 'N/A'
  }
  if (Array.isArray(data)) {
    const item = data.find(item => item && typeof item === 'string' && item.includes(prefix))
    console.log(`[extractValue] Found item:`, item)
    if (item) {
      let value = item.replace(prefix, '').trim()
      console.log(`[extractValue] Extracted value after removing prefix:`, value)

      // Special handling for percentage values (e.g., "Yield: 3.42%")
      if (value.includes('%')) {
        console.log(`[extractValue] Percentage value detected, returning as-is:`, value)
        return value
      }

      const match = value.match(/(\$?)([0-9.,]+)\s*([mkMK]?)/)
      if (match) {
        let num = parseFloat(match[2].replace(/,/g, ''))
        const unit = match[3].toLowerCase()
        if (unit === 'm') num *= 1_000_000
        else if (unit === 'k') num *= 1_000
        const result = `${match[1]}${num.toLocaleString('en-US', { maximumFractionDigits: 0 })}`
        console.log(`[extractValue] Final formatted result:`, result)
        return result
      }
      console.log(`[extractValue] No regex match, returning raw value:`, value)
      return value
    }
    console.log(`[extractValue] Item not found in array, returning N/A`)
    return 'N/A'
  }
  console.log(`[extractValue] Data is not array, returning as string:`, String(data))
  return String(data)
}

// Helper function to get value with fallback from multiple sources
export const getValueWithFallback = (primaryData, secondaryData, prefix = null) => {
  console.log(`[getValueWithFallback] prefix: "${prefix}", primaryData:`, primaryData, 'secondaryData:', secondaryData)

  // Try primary source first
  const primaryValue = prefix ? extractValue(primaryData, prefix) : primaryData
  console.log(`[getValueWithFallback] primaryValue extracted:`, primaryValue)

  // If primary is N/A or empty, try secondary source
  if (!primaryValue || primaryValue === 'N/A' || primaryValue === '') {
    const fallbackValue = prefix ? extractValue(secondaryData, prefix) : secondaryData
    console.log(`[getValueWithFallback] Using fallback value:`, fallbackValue)
    return fallbackValue
  }

  return primaryValue
}

// Helper function to extract property_com_au data from database format
export const getPropertyComAuData = (propertyComAuData) => {
  if (!propertyComAuData) return null

  // Check if it's array format with fetched_at
  if (Array.isArray(propertyComAuData) && propertyComAuData.length > 0) {
    const latestEntry = propertyComAuData[propertyComAuData.length - 1]
    return latestEntry?.data || latestEntry
  }

  // Direct object format
  return propertyComAuData
}

// Helper function to convert property_com_au data to domain-like format with fallback to onthehouse then domain data
export const convertPropertyComAuToDomainFormat = (propertyComAuData, domainData = null, onthehouseData = null) => {
  const data = getPropertyComAuData(propertyComAuData)

  // Debug logging
  console.log('[convertPropertyComAuToDomainFormat] property_com_au data:', data)
  console.log('[convertPropertyComAuToDomainFormat] domain data:', domainData)
  console.log('[convertPropertyComAuToDomainFormat] onthehouse data:', onthehouseData)

  // Helper to extract OnTheHouse rental value
  const getOnthehouseRentalValue = () => {
    if (!onthehouseData) return null

    const weeklyRent = getFeatureWithFallback(null, onthehouseData, 'weeklyRent')

    if (weeklyRent && weeklyRent !== 'N/A') {
      console.log('[convertPropertyComAuToDomainFormat] Using OnTheHouse rental fallback:', weeklyRent)
      return [`Weekly rent: ${weeklyRent}`]
    }
    return null
  }

  // If no property_com_au data, try onthehouse then domain data as fallback
  if (!data) {
    // Try OnTheHouse first
    if (onthehouseData) {
      console.log('[convertPropertyComAuToDomainFormat] Using onthehouse data as fallback (no property_com_au)')
      const onthehouseRental = getOnthehouseRentalValue()
      if (onthehouseRental) {
        return {
          rental_estimate: onthehouseRental,
          value_estimate: domainData?.value_estimate || null,
          key_features: domainData?.key_features || null,
          property_history: domainData?.property_history || null
        }
      }
    }

    // Then try domain
    if (domainData) {
      console.log('[convertPropertyComAuToDomainFormat] Using domain data as fallback (no property_com_au or onthehouse)')
      return domainData
    }

    console.log('[convertPropertyComAuToDomainFormat] No data available from any source')
    return null
  }

  // Helper to get domain data if available
  const getDomainData = (field) => {
    if (!domainData) return null
    const value = domainData[field] || null
    if (value) {
      console.log(`[convertPropertyComAuToDomainFormat] Using domain fallback for ${field}:`, value)
    }
    return value
  }

  return {
    // Property features - convert to domain "Key: Value" array format with fallback
    key_features: (() => {
      const features = [
        data.bedrooms ? `Bedrooms: ${data.bedrooms}` : null,
        data.bathrooms ? `Bathrooms: ${data.bathrooms}` : null,
        data.car_spaces ? `Car spaces: ${data.car_spaces}` : null,
        data.property_type ? `Property type: ${data.property_type}` : null,
        data.floor_area ? `Floor area: ${data.floor_area}` : null,
        data.land_size ? `Land area: ${data.land_size}` : null,
        data.build_year ? `Built year: ${data.build_year}` : null
      ].filter(Boolean)

      // Fallback to domain key_features if property_com_au has no features
      return features.length > 0 ? features : getDomainData('key_features')
    })(),

    // Value estimate - convert to domain format with fallback
    value_estimate: (() => {
      if (data.property_value_main || data.property_value_range_min || data.property_value_range_max) {
        const propertyComAuArray = [
          data.property_value_confidence ? `Accuracy: ${data.property_value_confidence}` : null,
          data.property_value_range_max ? `High: ${data.property_value_range_max}` : null,
          data.property_value_range_min ? `Low: ${data.property_value_range_min}` : null,
          data.property_value_main ? `Mid: ${data.property_value_main}` : null,
          data.property_value_updated ? `Last updated: ${data.property_value_updated}` : null
        ].filter(Boolean)

        // Check if the data is valid (has a valid Mid value)
        const hasValidValue = propertyComAuArray.some(item => {
          if (typeof item === 'string' && item.includes('Mid:')) {
            const value = item.replace('Mid:', '').trim()
            // Check if it's not "$-" or empty or just "$" or contains "No estimate"
            return value && value !== '$-' && value !== '$' && value !== '-' && !value.includes('No estimate')
          }
          return false
        })

        // If property_com_au has valid value data, use it; otherwise fallback to domain
        if (propertyComAuArray.length > 0 && hasValidValue) {
          console.log('[convertPropertyComAuToDomainFormat] Using property_com_au value_estimate:', propertyComAuArray)
          return propertyComAuArray
        } else if (!hasValidValue) {
          console.log('[convertPropertyComAuToDomainFormat] property_com_au value data is invalid, falling back to domain')
        }
      }
      // Fallback to domain value_estimate
      const domainValue = getDomainData('value_estimate')
      if (domainValue) {
        console.log('[convertPropertyComAuToDomainFormat] Using domain value_estimate fallback:', domainValue)
      }
      return domainValue
    })(),

    // Rental estimate - convert to domain format with fallback: Property.com.au -> OnTheHouse -> Domain
    rental_estimate: (() => {
      // First try Property.com.au
      if (data.rental_income_main || data.rental_income_range_min || data.rental_income_range_max) {
        // Calculate Yield if we have both property value and rental income
        let yieldValue = null
        if (data.rental_income_main && data.property_value_main) {
          // Parse values - remove $ and commas
          const parsePrice = (str) => {
            if (!str) return null
            const cleaned = String(str).replace(/[$,]/g, '').trim()
            const num = parseFloat(cleaned)
            return isNaN(num) ? null : num
          }
          const weeklyRent = parsePrice(data.rental_income_main)
          const propertyValue = parsePrice(data.property_value_main)

          if (weeklyRent && propertyValue && propertyValue > 0) {
            // Gross Rental Yield = (Annual Rent / Property Value) x 100
            yieldValue = ((weeklyRent * 52) / propertyValue * 100).toFixed(2)
          }
        }

        const propertyComAuArray = [
          data.rental_income_confidence ? `Accuracy: ${data.rental_income_confidence}` : null,
          data.rental_income_range_max ? `High: ${data.rental_income_range_max}` : null,
          data.rental_income_range_min ? `Low: ${data.rental_income_range_min}` : null,
          data.rental_income_main ? `Weekly rent: ${data.rental_income_main}` : null,
          yieldValue ? `Yield: ${yieldValue}` : null,  // Add calculated yield
          data.rental_income_updated ? `Last updated: ${data.rental_income_updated}` : null
        ].filter(Boolean)

        // Check if the data is valid (not "No estimate" or "$-")
        const hasValidRental = propertyComAuArray.some(item => {
          if (typeof item === 'string' && item.includes('Weekly rent:')) {
            const value = item.replace('Weekly rent:', '').trim()
            // Check if it's not "$-" or empty or just "$"
            return value && value !== '$-' && value !== '$' && value !== '-' && !value.includes('No estimate')
          }
          return false
        })

        // If property_com_au has valid rental data, use it
        if (propertyComAuArray.length > 0 && hasValidRental) {
          console.log('[convertPropertyComAuToDomainFormat] Using property_com_au rental_estimate:', propertyComAuArray)
          return propertyComAuArray
        } else if (!hasValidRental) {
          console.log('[convertPropertyComAuToDomainFormat] property_com_au rental data is invalid, trying OnTheHouse fallback')
        }
      }

      // Fallback to OnTheHouse
      const onthehouseRental = getOnthehouseRentalValue()
      if (onthehouseRental) {
        console.log('[convertPropertyComAuToDomainFormat] Using OnTheHouse rental_estimate fallback:', onthehouseRental)
        return onthehouseRental
      }

      // Final fallback to domain rental_estimate
      const domainRental = getDomainData('rental_estimate')
      if (domainRental) {
        console.log('[convertPropertyComAuToDomainFormat] Using domain rental_estimate fallback:', domainRental)
      }
      return domainRental
    })(),

    // Property history - convert to domain format with fallback
    property_history: (() => {
      if (data.property_history && Array.isArray(data.property_history) && data.property_history.length > 0) {
        return data.property_history.map(h => ({
          transaction_type: h.event_type || 'Record',
          price: h.price || 'Price not disclosed',
          date: h.datetime || h.year || 'Date unknown',
          agency: h.details ? h.details.split('by ')[1] || 'Agency unknown' : 'Agency unknown',
          method: null,
          days_on_market: null
        }))
      }
      // Fallback to domain property_history
      return getDomainData('property_history')
    })(),

    // Sold properties with fallback
    sold_properties: data.sold_properties || getDomainData('sold_properties'),

    // Suburb profile data with fallback
    suburb_profile_data: data.suburb_profile_data || getDomainData('suburb_profile_data'),

    // About property with fallback
    about: data.about_property || getDomainData('about'),

    // Address with fallback
    address: (() => {
      if (data.address_line1 && data.address_line2) {
        return [`${data.address_line1}, ${data.address_line2}`]
      }
      return getDomainData('address')
    })(),

    // Property type with fallback
    property_type: data.property_type || getDomainData('property_type'),

    // Additional fields for compatibility with fallback
    bedrooms: data.bedrooms || getDomainData('bedrooms'),
    bathrooms: data.bathrooms || getDomainData('bathrooms'),
    car_spaces: data.car_spaces || getDomainData('car_spaces'),
    land_size: data.land_size || getDomainData('land_size'),
    floor_area: data.floor_area || getDomainData('floor_area'),
    build_year: data.build_year || getDomainData('build_year')
  }
}

export function parseOnTheHouseSalesNearby(htmlString) {
  if (!htmlString || typeof htmlString !== 'string') return []

  try {
    // Create a temporary DOM element to parse HTML
    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = htmlString

    const properties = []
    const propertyCards = tempDiv.querySelectorAll('.PropertyCardSearch__propertyCard--FqRCV')

    propertyCards.forEach(card => {
      try {
        // Extract image
        const imgElement = card.querySelector('img[alt]')
        const imageUrl = imgElement?.src || null

        // Extract address from alt text or address elements
        const addressElements = card.querySelectorAll('.PropertyCardSearch__propertyCardAddressText--Cav7-')
        const address = Array.from(addressElements).map(el => el.textContent.trim()).join(' ').trim()

        // Extract price
        const priceElement = card.querySelector('.PropertyCardSearch__propertyCardPrice--EhKc+ span')
        const priceText = priceElement?.textContent.trim() || 'Price not available'

        // Extract sold date from ribbon
        const ribbonElement = card.querySelector('.PropertyCardSearch__ribbon--J6IKm')
        const soldDate = ribbonElement?.textContent.trim() || ''

        // Extract property features
        const features = {}
        const attributeElements = card.querySelectorAll('.PropertyCardSearch__propertyCardAttributes--KEdQl li')

        attributeElements.forEach(li => {
          const text = li.textContent.trim()
          if (text.includes('Bedrooms:')) {
            features.beds = text.replace('Bedrooms:', '').trim()
          } else if (text.includes('Bathrooms:')) {
            features.baths = text.replace('Bathrooms:', '').trim()
          } else if (text.includes('Car spaces:')) {
            features.parking = text.replace('Car spaces:', '').trim()
          } else if (text.includes('Land area:')) {
            features.landSize = text.replace('Land area:', '').trim()
          }
        })

        // Extract property type
        const typeElement = card.querySelector('[data="seoData"]:last-child')
        const propertyType = typeElement?.parentElement?.textContent.replace('Type:', '').trim() || 'House'

        // Extract agent info
        const agentNameElement = card.querySelector('.PropertyCardSearch__agentRep--aoR6q .bold500')
        const agencyElement = card.querySelector('.PropertyCardSearch__agencyName--B+iVM')
        const agentName = agentNameElement?.textContent.trim() || 'Agent not disclosed'
        const agencyName = agencyElement?.textContent.trim() || ''

        if (address) {
          properties.push({
            address,
            price_text: priceText,
            sold_date: soldDate,
            image_url: imageUrl,
            property_type: propertyType,
            features: [features],
            agent: {
              name: agentName,
              agency: agencyName
            },
            source: 'OnTheHouse'
          })
        }
      } catch (err) {

      }
    })

    return properties
  } catch (error) {

    return []
  }
}
